import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import Select from "react-select";
import { fetchJvByRestLink, fetchJvClaims } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { camelCaseToWords } from "../../utils/commonUtils";

const searchClaimOptions = [
    "Claimnumber",
    "Edibatchnumber",
    "AlternateID1",
    "AlternateID2",
    "SecondaryID",
    "PatientID",
    "ProviderID",
].map((str) => ({ value: str, label: camelCaseToWords(str) }));

const asJvArray = (value) => {
    if (value == null || value === "") return [];
    if (Array.isArray(value)) return value.filter((item) => item != null);
    if (typeof value === "object") return [value];
    return [];
};

const JvClaims = () => {
    const [searchValue, setSearchValue] = useState();
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();
    const [selectedSearchOption, setSelectedSearchOption] = useState();
    const { ref, inView } = useInView();

    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage,
        status,
    } = useInfiniteQuery({
        queryKey: ["jv-claims", selectedSearchOption, decouncedSearchValue],
        queryFn: async ({ pageParam }) => {
            let params = pageParam || {};

            if (decouncedSearchValue && selectedSearchOption?.value) {
                params[selectedSearchOption.value] = decouncedSearchValue;
            }

            return await fetchJvClaims(params);
        },
        select: (data) => ({
            rows: data.pages
                .map((page) => page.Response?.AdjudicationResult)
                .flatMap((x) => asJvArray(x))
                .filter((x) => Object.keys(x)?.length),
        }),
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            const nextPageUrl = lastPage?.Response?.Pagination?.Next;
            if (!nextPageUrl) return undefined;
            return {
                nextPageUrl: nextPageUrl.split("/rest/")[1],
            };
        },
        staleTime: 0,
        retry: false,
        enabled: !!(selectedSearchOption && decouncedSearchValue),
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event?.target?.value?.trim());
        }, 500),
        []
    );

    const columns = [
        "Claimnumber",
        "Claimstatus",
        "Product",
        "Patientfirstname",
        "Patientlastname",
        "Providerofficename",
        "PrimaryICD",
        "Incurreddate",
        "Receiveddate",
        "TotCharge",
        "TotPaid",
        "ClientID",
    ];

    const onDetailHandler = async (row) => {
        if (row?.ClaimLink) {
            return await fetchJvByRestLink(row.ClaimLink);
        }
        if (row?.Claimnumber) {
            return await fetchJvClaims({ Claimnumber: row.Claimnumber });
        }
        return row;
    };

    return (
        <div className={"container "}>
            <p className="title">Claims</p>
            <p className="sub-title">JV claims</p>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1rem 1rem 0rem" }}>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", width: "100%", flexWrap: "wrap" }}>
                    <Select
                        name={"claimSearch"}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={"Search"}
                        isClearable={true}
                        options={searchClaimOptions}
                        value={selectedSearchOption}
                        onChange={(option) => {
                            setSelectedSearchOption(option);
                            setSearchValue("");
                            setDecouncedSearchValue("");
                        }}
                    />

                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder="search..."
                        autoComplete="off"
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setSelectedSearchOption(null);
                        setSearchValue("");
                        setDecouncedSearchValue("");
                    }}
                >
                    Clear filters
                </p>
            </div>

            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className="danger">Error: {error.message}</span>
            ) : (
                <SharedTable
                    type={"jv-claims"}
                    columns={columns}
                    disableHeaderFormatting={true}
                    items={data?.rows}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    onDetailHandler={onDetailHandler}
                    ref={ref}
                    noResultsMessage={selectedSearchOption ? "No claims found. Try changing the filter." : "Search by claim number, patient ID, provider ID, or batch to load claims."}
                />
            )}
        </div>
    );
};

export default JvClaims;
